import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface Quiz {
  id: string;
  interview_id: string;
  questions: QuizQuestion[];
  created_at: string;
  updated_at: string;
}

export interface QuizAttempt {
  id: string;
  quiz_id: string;
  user_id?: string;
  session_id?: string;
  score: number;
  total_questions: number;
  answers: Record<number, number>;
  completed_at: string;
  time_taken_seconds?: number;
}

export const useQuiz = (interviewId: string) => {
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuiz = async () => {
    if (!interviewId) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('quizzes')
        .select('*')
        .eq('interview_id', interviewId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }

      setQuiz(data ? {
        ...data,
        questions: data.questions as unknown as QuizQuestion[]
      } as Quiz : null);
    } catch (err) {
      console.error('Error fetching quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch quiz');
    } finally {
      setIsLoading(false);
    }
  };

  const generateQuiz = async () => {
    if (!interviewId) {
      console.error('No interview ID provided');
      setError('Interview ID er påkrevd');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);
      toast.info('Genererer quiz-spørsmål...');

      console.log('Generating quiz for interview:', interviewId);
      const requestBody = { interviewId };
      console.log('Request payload:', requestBody);

      const { data, error } = await supabase.functions.invoke('generate-quiz', {
        body: requestBody
      });

      console.log('Response data:', data);
      console.log('Response error:', error);

      if (error) {
        console.error('Error generating quiz:', error);
        throw error;
      }

      if (data?.error) {
        console.error('Quiz generation failed:', data);
        throw new Error(data.error);
      }

      setQuiz({
        ...data.quiz,
        questions: data.quiz.questions as unknown as QuizQuestion[]
      } as Quiz);
      
      // Refetch to ensure we have the latest data
      await fetchQuiz();
      toast.success('Quiz generert!');
    } catch (err) {
      console.error('Error generating quiz:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate quiz';
      setError(errorMessage);
      toast.error(`Kunne ikke generere quiz: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const submitQuizAttempt = async (
    answers: Record<number, number>,
    timeSpent?: number,
    shuffledCorrectIndices?: Record<number, number>
  ) => {
    if (!quiz) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Calculate score using shuffled correct indices if provided, otherwise fall back to original
      let score = 0;
      quiz.questions.forEach((question, index) => {
        const correctIndex = shuffledCorrectIndices?.[index] ?? question.correctAnswer;
        if (answers[index] === correctIndex) {
          score++;
        }
      });

      const sessionId = user ? undefined : crypto.randomUUID();

      const { data, error } = await supabase
        .from('quiz_attempts')
        .insert({
          quiz_id: quiz.id,
          user_id: user?.id,
          session_id: sessionId,
          score,
          total_questions: quiz.questions.length,
          answers,
          time_taken_seconds: timeSpent
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (err) {
      console.error('Error submitting quiz attempt:', err);
      throw err;
    }
  };

  useEffect(() => {
    fetchQuiz();
  }, [interviewId]);

  return {
    quiz,
    isLoading,
    isGenerating,
    error,
    generateQuiz,
    submitQuizAttempt,
    refetch: fetchQuiz
  };
};